import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useEmployeeAuthContext } from "./useEmployeeAuthContext";
import API_URL from "../api/API";


// ============================================================
// TIMEZONE
// ============================================================

const TIMEZONE = "America/Chicago";


// ============================================================
// FORMAT DATE
// ============================================================

const formatDate = (date) => {

    if (!date) return "";

    return new Intl.DateTimeFormat(
        "en-US",
        {
            weekday: "long",
            month: "long",
            day: "numeric",
            timeZone: TIMEZONE,
        }
    ).format(new Date(date));

};

// ============================================================
// FORMAT TIME ENTRY SUMMARY DAY
// ============================================================

const formatSummaryDay = (dateKey) => {

    if (!dateKey) {
        return {
            weekday: "",
            monthDay: "",
        };
    }


    const [
        year,
        month,
        day
    ] = dateKey
        .split("-")
        .map(Number);


    const safeDate = new Date(
        year,
        month - 1,
        day,
        12,
        0,
        0
    );


    const weekday =
        new Intl.DateTimeFormat(
            "en-US",
            {
                weekday: "short",
                timeZone: TIMEZONE,
            }
        ).format(safeDate);


    const monthDay =
        new Intl.DateTimeFormat(
            "en-US",
            {
                month: "short",
                day: "numeric",
                timeZone: TIMEZONE,
            }
        ).format(safeDate);


    return {
        weekday,
        monthDay,
    };

};

// ============================================================
// GET MONDAY OF WEEK
// ============================================================

const getMondayDateKey = (dateKey) => {

    if (!dateKey) {
        return null;
    }


    const [
        year,
        month,
        day
    ] = dateKey
        .split("-")
        .map(Number);


    const date = new Date(
        year,
        month - 1,
        day,
        12,
        0,
        0
    );


    const dayOfWeek =
        date.getDay();


    // Sunday = 0
    // Monday = 1
    //
    // Convert Sunday to 7 so we can
    // calculate days since Monday.

    const daysSinceMonday =
        dayOfWeek === 0
            ? 6
            : dayOfWeek - 1;


    date.setDate(
        date.getDate() -
        daysSinceMonday
    );


    return [
        date.getFullYear(),
        String(
            date.getMonth() + 1
        ).padStart(2, "0"),
        String(
            date.getDate()
        ).padStart(2, "0"),
    ].join("-");
};

// ============================================================
// GET ACTIVE 14-DAY PAY PERIOD START
// ============================================================

const PAY_PERIOD_ANCHOR_DATE = "2026-07-20";

const getActivePayPeriodStartDateKey = (
    todayDateKey
) => {

    const [
        anchorYear,
        anchorMonth,
        anchorDay
    ] = PAY_PERIOD_ANCHOR_DATE
        .split("-")
        .map(Number);

    const [
        todayYear,
        todayMonth,
        todayDay
    ] = todayDateKey
        .split("-")
        .map(Number);

    const anchorDate = new Date(
        anchorYear,
        anchorMonth - 1,
        anchorDay,
        12,
        0,
        0
    );

    const todayDate = new Date(
        todayYear,
        todayMonth - 1,
        todayDay,
        12,
        0,
        0
    );

    const daysSinceAnchor = Math.floor(
        (
            todayDate.getTime() -
            anchorDate.getTime()
        ) /
        (1000 * 60 * 60 * 24)
    );

    const periodNumber = Math.floor(
        daysSinceAnchor / 14
    );

    const periodStartDate = new Date(
        anchorDate
    );

    periodStartDate.setDate(
        periodStartDate.getDate() +
        (periodNumber * 14)
    );

    return [
        periodStartDate.getFullYear(),
        String(
            periodStartDate.getMonth() + 1
        ).padStart(2, "0"),
        String(
            periodStartDate.getDate()
        ).padStart(2, "0"),
    ].join("-");
};


// ============================================================
// GROUP TIME SUMMARY BY MONDAY → SUNDAY
// ============================================================

const getTimeSummaryWeeks = (days) => {

    if (
        !days ||
        !Array.isArray(days)
    ) {
        return [];
    }


    const weeks = {};


    days.forEach(
        (day) => {

            const mondayDateKey =
                getMondayDateKey(
                    day.date
                );


            if (!mondayDateKey) {
                return;
            }


            if (!weeks[mondayDateKey]) {

                weeks[mondayDateKey] = [];

            }


            weeks[mondayDateKey].push(
                day
            );

        }
    );


    return Object.entries(
        weeks
    )
        .sort(
            (
                [dateA],
                [dateB]
            ) =>
                dateA.localeCompare(
                    dateB
                )
        )
        .map(
            (
                [
                    mondayDateKey,
                    weekDays
                ],
                index
            ) => ({

                label:
                    `Week ${index + 1}`,

                mondayDateKey,

                days:
                    weekDays.sort(
                        (
                            a,
                            b
                        ) =>
                            a.date.localeCompare(
                                b.date
                            )
                    ),

            })
        );

};


// ============================================================
// FORMAT TIME ENTRY SUMMARY DATE
// ============================================================

const formatSummaryDate = (dateKey) => {

    if (!dateKey) {
        return "";
    }

    const [
        year,
        month,
        day
    ] = dateKey
        .split("-")
        .map(Number);

    const safeDate = new Date(
        year,
        month - 1,
        day,
        12,
        0,
        0
    );

    return new Intl.DateTimeFormat(
        "en-US",
        {
            weekday: "long",
            month: "long",
            day: "numeric",
            year: "numeric",
            timeZone: TIMEZONE,
        }
    ).format(safeDate);

};



// ============================================================
// FORMAT TIME
// ============================================================

const formatTime = (date) => {

    if (!date) return "";

    return new Intl.DateTimeFormat(
        "en-US",
        {
            hour: "numeric",
            minute: "2-digit",
            timeZone: TIMEZONE,
        }
    ).format(new Date(date));

};




// ============================================================
// EMPLOYEE DASHBOARD
// ============================================================

export const EmployeeDashboard = () => {

    const { employee, logout } =
        useEmployeeAuthContext();

    const navigate =
        useNavigate();


    // ============================================================
    // GENERAL STATE
    // ============================================================

    const [stores, setStores] =
        useState([]);

    const [selectedStore, setSelectedStore] =
        useState("");

    const [isClockedIn, setIsClockedIn] =
        useState(false);

    const [isLoadingStores, setIsLoadingStores] =
        useState(true);

    const [
        isLoadingTimeEntry,
        setIsLoadingTimeEntry
    ] = useState(true);

    const [isProcessing, setIsProcessing] =
        useState(false);

        const [showProfileMenu, setShowProfileMenu] = useState(false);


        const [showPinModal, setShowPinModal] = useState(false);

        const [verificationPin, setVerificationPin] = useState("");

        const [pinError, setPinError] = useState("");

        const [isVerifyingPin, setIsVerifyingPin] = useState(false);

        const pinInputRef = useRef(null);

    const [error, setError] =
        useState("");

    const [success, setSuccess] =
        useState("");

        // ============================================================
        // CHANGE REQUEST STATE
        // ============================================================

        const [showChangeRequestModal, setShowChangeRequestModal] =
        useState(false);

        const [changeRequestMessage, setChangeRequestMessage] =
        useState("");

        const [isSubmittingChangeRequest, setIsSubmittingChangeRequest] =
        useState(false);


    // ============================================================
    // CURRENT TIME ENTRY DETAILS
    // ============================================================

    const [
        currentTimeEntry,
        setCurrentTimeEntry
    ] = useState(null);

    const [lastTimeEntry, setLastTimeEntry] = useState(null);


    // ============================================================
    // LOCATION STATE
    // ============================================================

    const [
        locationPermission,
        setLocationPermission
    ] = useState("unknown");

    const [isManualMode, setIsManualMode] =
        useState(false);

    const [
        locationMessage,
        setLocationMessage
    ] = useState("");

    const [
        isRequestingLocation,
        setIsRequestingLocation
    ] = useState(false);

    const [employeeTimeSummary, setEmployeeTimeSummary] = useState(null);
const [isLoadingTimeSummary, setIsLoadingTimeSummary] = useState(false);
const [showTimeSummaryModal, setShowTimeSummaryModal] = useState(false);


// ============================================================
// LOCK PAGE SCROLL WHEN A MODAL IS OPEN
// ============================================================

useEffect(() => {

    const modalIsOpen =
        showPinModal ||
        showChangeRequestModal ||
        showTimeSummaryModal;


    if (!modalIsOpen) {
        return;
    }


    const originalOverflow =
        document.body.style.overflow;


    document.body.style.overflow =
        "hidden";


    return () => {

        document.body.style.overflow =
            originalOverflow;

    };

}, [
    showPinModal,
    showChangeRequestModal,
    showTimeSummaryModal
]);


    // ============================================================
    // CHECK LOCATION PERMISSION
    // ============================================================

    const checkLocationPermission = async () => {

        try {

            if (
                !navigator.permissions ||
                !navigator.permissions.query
            ) {

                setLocationPermission("unknown");

                return;

            }


            const permission =
                await navigator.permissions.query({
                    name: "geolocation",
                });


            setLocationPermission(
                permission.state
            );


            permission.onchange = () => {

                setLocationPermission(
                    permission.state
                );


                if (
                    permission.state === "granted" ||
                    permission.state === "prompt"
                ) {

                    setLocationMessage("");

                    setIsManualMode(false);

                }


                if (
                    permission.state === "denied"
                ) {

                    setIsManualMode(true);

                    setLocationMessage(
                        "Location access is blocked for this site. You can enable it again or select your store manually."
                    );

                }

            };


        } catch (error) {

            console.warn(
                "Unable to check location permission:",
                error
            );

            setLocationPermission("unknown");

        }

    };


    // ============================================================
    // CHECK LOCATION PERMISSION ON LOAD
    // ============================================================

    useEffect(() => {

        checkLocationPermission();

    }, []);



    // ============================================================
    // GET ACTIVE STORES
    // ============================================================

    useEffect(() => {

        const getStores = async () => {

            try {

                const response = await fetch(

                    API_URL.FRONTEND_STORE_LIST,

                    {
                        method: "GET",

                        headers: {

                            Authorization:
                                `Bearer ${employee?.token}`,

                        },

                    }

                );


                const data =
                    await response.json();


                if (!response.ok) {

                    throw new Error(

                        data.message ||
                        data.error ||
                        "Unable to load stores."

                    );

                }


                setStores(
                    data.stores || []
                );


            } catch (error) {

                console.error(
                    "Get Stores Error:",
                    error
                );

                setError(
                    error.message
                );


            } finally {

                setIsLoadingStores(false);

            }

        };


        if (employee?.token) {

            getStores();

        }

    }, [employee]);


    // ============================================================
    // GET CURRENT TIME ENTRY
    // ============================================================

    useEffect(() => {

        const getCurrentTimeEntry = async () => {

            try {

                setIsLoadingTimeEntry(true);


                const response = await fetch(

                    `${API_URL.FRONTEND_EMPLOYEE_CURRENT_TIMEENTRY}?employeeId=${employee?.id}`,

                    {
                        method: "GET",

                        headers: {

                            Authorization:
                                `Bearer ${employee?.token}`,

                        },

                    }

                );


                const data =
                    await response.json();


                if (!response.ok) {

                    throw new Error(

                        data.message ||
                        data.error ||
                        "Unable to get current time entry."

                    );

                }


                setIsClockedIn(
                    data.isClockedIn
                );


                setCurrentTimeEntry(
                    data.timeEntry || null
                );

                setLastTimeEntry(
                    data.lastTimeEntry || null
                );


                if (
                    data.isClockedIn &&
                    data.timeEntry
                ) {

                    setSelectedStore(

                        data.timeEntry.store?._id ||
                        data.timeEntry.store ||
                        ""

                    );

                }


            } catch (error) {

                console.error(
                    "Get Current Time Entry Error:",
                    error
                );

                setError(
                    error.message
                );


            } finally {

                setIsLoadingTimeEntry(false);

            }

        };


        if (
            employee?.token &&
            employee?.id
        ) {

            getCurrentTimeEntry();

        }

    }, [employee]);

// ============================================================
// GET EMPLOYEE TIME SUMMARY
// ============================================================

const getEmployeeTimeSummary = async () => {

    if (!employee?.token) {
        return;
    }


    try {

        setIsLoadingTimeSummary(true);

        setError("");


        const response = await fetch(

            API_URL.FRONTEND_EMPLOYEE_TIME_SUMMARY,

            {
                method: "GET",

                headers: {

                    Authorization:
                        `Bearer ${employee?.token}`,

                },

            }

        );


        const data =
            await response.json();


        if (!response.ok) {

            throw new Error(

                data.message ||
                data.error ||
                "Unable to load your time entries."

            );

        }


       // ============================================================
// SHOW ONLY CURRENT PAY PERIOD
// ============================================================

const todayDateKey = data.endDate;

const periodStartDateKey =
    getActivePayPeriodStartDateKey(
        todayDateKey
    );

const filteredDays = (
    data.days || []
).filter(
    (day) =>
        day.date >= periodStartDateKey &&
        day.date <= todayDateKey
);


setEmployeeTimeSummary({

    ...data,

    startDate:
        periodStartDateKey,

    endDate:
        todayDateKey,

    days:
        filteredDays,

});


setShowTimeSummaryModal(
    true
);


    } catch (error) {

        console.error(
            "Get Employee Time Summary Error:",
            error
        );


        setError(
            error.message ||
            "Unable to load your time entries."
        );


    } finally {

        setIsLoadingTimeSummary(
            false
        );

    }

};

    // ============================================================
    // GET CURRENT GPS LOCATION
    // ============================================================

    const getCurrentLocation = () => {

        return new Promise(
            (resolve, reject) => {

                if (!navigator.geolocation) {

                    reject({

                        code: 2,

                        message:
                            "Geolocation is not supported by this browser.",

                    });

                    return;

                }


                navigator.geolocation.getCurrentPosition(

                    (position) => {

                        resolve({

                            latitude:
                                position.coords.latitude,

                            longitude:
                                position.coords.longitude,

                        });

                    },


                    (error) => {

                        reject(error);

                    },


                    {

                        enableHighAccuracy: true,

                        timeout: 10000,

                        maximumAge: 0,

                    }

                );

            }

        );

    };


    // ============================================================
    // HANDLE LOCATION UNAVAILABLE
    // ============================================================

    const handleLocationUnavailable =
        (locationError) => {

            console.error(
                "Location Error:",
                locationError
            );


            if (locationError?.code === 1) {

                setLocationPermission(
                    "denied"
                );

                setIsManualMode(true);

                setLocationMessage(

                    "Location access is blocked for this site. You can enable Location in your browser's site settings and try GPS again, or select your store manually."

                );

                return;

            }


            if (locationError?.code === 2) {

                setLocationPermission(
                    "unknown"
                );

                setIsManualMode(true);

                setLocationMessage(

                    "We could not determine your current location. You can try GPS again or select your store manually."

                );

                return;

            }


            if (locationError?.code === 3) {

                setLocationPermission(
                    "unknown"
                );

                setIsManualMode(true);

                setLocationMessage(

                    "Getting your location took too long. You can try GPS again or select your store manually."

                );

                return;

            }


            setIsManualMode(true);

            setLocationMessage(

                "We could not get your current location. You can try GPS again or select your store manually."

            );

        };


    // ============================================================
    // TRY GPS AGAIN
    // ============================================================

    const tryGPSAgain = async () => {

        setError("");

        setSuccess("");

        setLocationMessage("");

        setIsRequestingLocation(true);


        try {

            const location =
                await getCurrentLocation();


            if (location) {

                setLocationPermission(
                    "granted"
                );

                setIsManualMode(false);

                setLocationMessage("");

            }


        } catch (locationError) {

            handleLocationUnavailable(
                locationError
            );

        } finally {

            setIsRequestingLocation(false);

        }

    };


    // ============================================================
    // CLOCK IN USING GPS
    // ============================================================

    const clockInUsingGPS = async () => {

        setIsRequestingLocation(true);

        setError("");

        setSuccess("");

        setLocationMessage("");


        try {

            const location =
                await getCurrentLocation();


            setLocationPermission(
                "granted"
            );


            const response = await fetch(

                API_URL.FRONTEND_EMPLOYEE_TIMEENTRY,

                {

                    method: "POST",

                    headers: {

                        "Content-Type":
                            "application/json",

                        Authorization:
                            `Bearer ${employee?.token}`,

                    },

                    body: JSON.stringify({

                        employeeId:
                            employee.id,

                        storeSelection:
                            "gps",

                        latitude:
                            location.latitude,

                        longitude:
                            location.longitude,

                    }),

                }

            );


            const data =
                await response.json();


            if (!response.ok) {

                throw new Error(

                    data.message ||
                    data.error ||
                    "Unable to clock in."

                );

            }


            if (
                data.status === "Working"
            ) {

                setIsClockedIn(true);

                setIsManualMode(false);

                setLocationMessage("");

                setCurrentTimeEntry(
                    data.timeEntry || null
                );


                setSelectedStore(

                    data.timeEntry?.store?._id ||
                    data.timeEntry?.store ||
                    ""

                );


                setSuccess(
                    "You have been clocked in successfully."
                );

            }


        } catch (error) {

            console.error(
                "GPS Clock In Error:",
                error
            );


            if (

                error?.code === 1 ||
                error?.code === 2 ||
                error?.code === 3

            ) {

                handleLocationUnavailable(
                    error
                );

            } else {

                setError(
                    error.message
                );

            }


        } finally {

            setIsRequestingLocation(false);

        }

    };


    // ============================================================
    // CLOCK IN MANUALLY
    // ============================================================

    const clockInManually = async () => {

        setError("");

        setSuccess("");


        if (!selectedStore) {

            setError(
                "Please select a store before clocking in."
            );

            return;

        }


        setIsProcessing(true);


        try {

            const response = await fetch(

                API_URL.FRONTEND_EMPLOYEE_TIMEENTRY,

                {

                    method: "POST",

                    headers: {

                        "Content-Type":
                            "application/json",

                        Authorization:
                            `Bearer ${employee?.token}`,

                    },

                    body: JSON.stringify({

                        employeeId:
                            employee.id,

                        storeId:
                            selectedStore,

                        storeSelection:
                            "manual",

                    }),

                }

            );


            const data =
                await response.json();


            if (!response.ok) {

                throw new Error(

                    data.message ||
                    data.error ||
                    "Unable to clock in."

                );

            }


            if (
                data.status === "Working"
            ) {

                setIsClockedIn(true);

                setIsManualMode(false);

                setLocationMessage("");

                setCurrentTimeEntry(
                    data.timeEntry || null
                );


                setSuccess(
                    "You have been clocked in successfully."
                );

            }


        } catch (error) {

            console.error(
                "Manual Clock In Error:",
                error
            );

            setError(
                error.message
            );


        } finally {

            setIsProcessing(false);

        }

    };


    // ============================================================
    // CLOCK OUT
    // ============================================================

    const clockOut = async () => {

        setIsProcessing(true);

        setError("");

        setSuccess("");


        try {

            let location = null;


            try {

                location =
                    await getCurrentLocation();

            } catch (locationError) {

                console.warn(
                    "Clock-out GPS unavailable:",
                    locationError
                );

            }


            const requestBody = {

                employeeId:
                    employee.id,

            };


            if (location) {

                requestBody.latitude =
                    location.latitude;

                requestBody.longitude =
                    location.longitude;

            }


            const response = await fetch(

                API_URL.FRONTEND_EMPLOYEE_TIMEENTRY,

                {

                    method: "POST",

                    headers: {

                        "Content-Type":
                            "application/json",

                        Authorization:
                            `Bearer ${employee?.token}`,

                    },

                    body:
                        JSON.stringify(
                            requestBody
                        ),

                }

            );


            const data =
                await response.json();


            if (!response.ok) {

                throw new Error(

                    data.message ||
                    data.error ||
                    "Unable to clock out."

                );

            }


            if (
                data.status === "Clocked Out"
            ) {

                setIsClockedIn(false);

                setSelectedStore("");

                setCurrentTimeEntry(null);

                setIsManualMode(false);

                setLocationMessage("");

                setSuccess(
                    "You have been clocked out successfully."
                );

            }


        } catch (error) {

            console.error(
                "Clock Out Error:",
                error
            );

            setError(
                error.message
            );


        } finally {

            setIsProcessing(false);

        }

    };

    // ============================================================
// OPEN PIN VERIFICATION
// ============================================================

const openPinVerification = () => {

    setError("");

    setSuccess("");

    setVerificationPin("");

    setPinError("");

    setShowPinModal(true);

};

useEffect(() => {

    if (showPinModal) {

        const timer = setTimeout(() => {

            pinInputRef.current?.focus();

        }, 100);

        return () => clearTimeout(timer);

    }

}, [showPinModal]);

// ============================================================
// CLOSE PIN VERIFICATION
// ============================================================

const closePinVerification = () => {

    if (isVerifyingPin) {

        return;

    }

    setShowPinModal(false);

    setVerificationPin("");

    setPinError("");

};


// ============================================================
// HANDLE PIN CHANGE
// ============================================================

const handleVerificationPinChange = (e) => {

    const value = e.target.value;

    if (/^\d{0,4}$/.test(value)) {

        setVerificationPin(value);

        setPinError("");

    }

};


// ============================================================
// VERIFY PIN AND CONTINUE CLOCK ACTION
// ============================================================

const verifyPinAndClock = async (e) => {

    e.preventDefault();

    setPinError("");


    if (verificationPin.length !== 4) {

        setPinError(
            "Please enter your 4-digit PIN."
        );

        return;

    }


    setIsVerifyingPin(true);


    try {

        const response = await fetch(

            API_URL.FRONTEND_EMPLOYEE_VERIFYPIN,

            {

                method: "POST",

                headers: {

                    "Content-Type":
                        "application/json",

                    Authorization:
                        `Bearer ${employee?.token}`,

                },

                body: JSON.stringify({

                    employeeId:
                        employee?.id,

                    pin:
                        verificationPin,

                }),

            }

        );


        const data =
            await response.json();


        if (!response.ok) {

            throw new Error(

                data.error ||
                data.message ||
                "Incorrect PIN."

            );

        }


        // PIN IS CORRECT
        setShowPinModal(false);

        setVerificationPin("");

        await handleClockAction();


    } catch (error) {

        console.error(
            "PIN Verification Error:",
            error
        );

        setPinError(
            error.message ||
            "Unable to verify PIN."
        );

    } finally {

        setIsVerifyingPin(false);

    }

};


    // ============================================================
    // MAIN CLOCK ACTION
    // ============================================================

    const handleClockAction = async () => {

        setError("");

        setSuccess("");


        if (isClockedIn) {

            await clockOut();

            return;

        }


        if (

            isManualMode ||
            locationPermission === "denied"

        ) {

            await clockInManually();

            return;

        }


        await clockInUsingGPS();

    };


    // ============================================================
    // LOGOUT
    // ============================================================

    const handleLogout = () => {

        logout();

        navigate("/");

    };

    // ============================================================
// CHANGE REQUEST
// ============================================================

const openChangeRequestModal = () => {

    setError("");

    setSuccess("");

    setChangeRequestMessage("");

    setShowChangeRequestModal(true);

};


// ============================================================
// FORMAT SUMMARY HOURS
// ============================================================

const formatSummaryHours = (
    value
) => {

    if (
        value === null ||
        value === undefined ||
        value === ""
    ) {

        return "0:00";

    }


    return value;

};

// ============================================================
// CLOSE CHANGE REQUEST MODAL
// ============================================================

const closeChangeRequestModal = () => {

    if (isSubmittingChangeRequest) {

        return;

    }

    setShowChangeRequestModal(false);

    setChangeRequestMessage("");

};


// ============================================================
// SUBMIT CHANGE REQUEST
// ============================================================

const submitChangeRequest = async (e) => {

    e.preventDefault();

    setError("");

    setSuccess("");


    // --------------------------------------------------------
    // VALIDATE MESSAGE
    // --------------------------------------------------------

    if (!changeRequestMessage.trim()) {

        setError(
            "Please enter your change request."
        );

        return;

    }


    setIsSubmittingChangeRequest(true);


    try {

        const response = await fetch(

            API_URL.FRONTEND_EMPLOYEE_CHANGE_REQUEST,

            {

                method: "POST",

                headers: {

                    "Content-Type":
                        "application/json",

                    Authorization:
                        `Bearer ${employee?.token}`,

                },

                body: JSON.stringify({

                    employeeId:
                        employee?.id,

                    message:
                        changeRequestMessage.trim(),

                }),

            }

        );


        const data =
            await response.json();


        if (!response.ok) {

            throw new Error(

                data.message ||
                data.error ||
                "Unable to submit change request."

            );

        }


        // ----------------------------------------------------
        // SUCCESS
        // ----------------------------------------------------

        setShowChangeRequestModal(false);

        setChangeRequestMessage("");

        setSuccess(
            "Your change request has been submitted."
        );


    } catch (error) {

        console.error(
            "Submit Change Request Error:",
            error
        );

        setError(
            error.message ||
            "Unable to submit change request."
        );


    } finally {

        setIsSubmittingChangeRequest(false);

    }

};


    // ============================================================
    // SELECTED STORE DATA
    // ============================================================

    const selectedStoreData =
        stores.find(
            (store) =>
                store._id === selectedStore
        );


    // ============================================================
    // EMPLOYEE NAME
    // ============================================================

    const employeeName =
        `${employee?.firstname || ""} ${employee?.lastname || ""}`
            .trim();


    const employeeInitial =
        employee?.firstname?.charAt(0)?.toUpperCase() ||
        "S";


    // ============================================================
    // CLOCK-IN TIME
    // ============================================================

    const clockInDate =

        currentTimeEntry?.clockIn ||
        currentTimeEntry?.clockInTime ||
        currentTimeEntry?.createdAt;


    // ============================================================
    // LOADING
    // ============================================================

    if (isLoadingTimeEntry) {

        return (

            <div className="flex h-[100dvh] overflow-hidden items-center justify-center bg-[#faf8f6]">

                <div className="text-center">

                    <div className="text-[18px] font-semibold text-[#1f1b19]">

                        Loading...

                    </div>

                    <div className="mt-1 text-[14px] text-[#8a837d]">

                        Checking your status

                    </div>

                </div>

            </div>

        );

    }


    // ============================================================
    // RENDER
    // ============================================================

    return (

        <div
        className={`min-h-[100dvh] ${
            isClockedIn ? "bg-[#E6F6EC]" : "bg-[#faf8f6]"
        } text-[#1f1b19] ${
            isManualMode || locationPermission === "denied"
                ? "overflow-y-auto"
                : "h-[100dvh] overflow-hidden"
        }`}
    >



                {/* ====================================================
                ERROR MESSAGE
            ==================================================== */}

            {error && (

            <div className="fixed left-1/2 top-[76px] z-40 w-[calc(100%-32px)] max-w-[390px] -translate-x-1/2 rounded-2xl border border-red-200 bg-white px-5 py-4 shadow-lg">

                <div className="flex items-center gap-3">

                    {/* ERROR ICON */}

                    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-red-100 text-red-600">

                        !

                    </div>


                    {/* MESSAGE */}

                    <p className="flex-1 text-sm font-medium text-red-600">

                        {error}

                    </p>


                    {/* CLOSE BUTTON */}

                    <button
                        type="button"
                        onClick={() => setError("")}
                        aria-label="Close error message"
                        className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-[22px] font-normal leading-none text-[#8a837d] transition hover:bg-[#fdf0f0] hover:text-red-600"
                    >

                        ×

                    </button>

                </div>

            </div>

            )}

            {/* ====================================================
                MAIN MOBILE CONTAINER
            ==================================================== */}

<div
    className={`mx-auto flex w-full max-w-[430px] flex-col px-6 pb-4 ${
        isManualMode || locationPermission === "denied"
            ? "min-h-[100dvh]"
            : "h-[100dvh] overflow-hidden"
    }`}
>

                {/* ====================================================
                    HEADER
                ==================================================== */}

                <header className="flex items-center justify-between pt-4">

                    {/* LOGO */}

                    <div className="flex items-center gap-2.5">

                        <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-[#7a3ea3] text-sm font-bold text-white shadow-sm">

                            S

                        </div>

                        <span className="text-[18px] font-bold tracking-[-0.02em]">

                            SpaClock

                        </span>

                    </div>


                    {/* EMPLOYEE / LOGOUT */}
                    <div className="relative flex items-center gap-3">

                            <button
                                type="button"
                                onClick={() => setShowProfileMenu((prev) => !prev)}
                                title="Profile"
                                className="flex h-10 w-10 items-center justify-center rounded-full bg-[#ebe7e3] text-[15px] font-semibold text-[#5c5651] transition hover:bg-[#ddd7d2]"
                            >
                                {employeeInitial}
                            </button>

                            {showProfileMenu && (
                                <div className="absolute right-0 top-12 z-50 w-[150px] rounded-xl border border-[#e5dfda] bg-white p-2 shadow-lg">

                                    <div className="px-3 py-2">
                                        <p className="truncate text-[13px] font-semibold text-[#1f1b19]">
                                            {employeeName}
                                        </p>
                                    </div>

                                    <div className="my-1 h-px bg-[#eee9e5]" />

                                    <button
                                        type="button"
                                        onClick={handleLogout}
                                        className="flex w-full items-center rounded-lg px-3 py-2 text-left text-[14px] font-medium text-red-600 transition hover:bg-red-50"
                                    >
                                        Sign out
                                    </button>

                                </div>
                            )}

                        </div>

                </header>


                {/* ====================================================
                    MAIN CONTENT
                ==================================================== */}

                <main className="flex flex-1 flex-col">


                    {/* ====================================================
                        GREETING
                    ==================================================== */}


                    <section className="pt-[clamp(18px,5vh,40px)] text-center">

                        <p className="text-[15px] font-medium text-[#8a837d]">

                            Hi, {employee?.firstname || "Employee"}

                        </p>

                        </section>


                    {/* ====================================================
                        STATUS AREA
                    ==================================================== */}

                        <section className="pt-[28px] text-center">


                        {/* ================================================
                            WORKING
                        ================================================= */}

                        {isClockedIn && (

                            <>

                                {/* STATUS */}

                                <div className="flex items-center justify-center gap-3">

                                    <span className="relative flex h-4 w-4">

                                        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#2f9e6a] opacity-40" />

                                        <span className="relative inline-flex h-4 w-4 rounded-full bg-[#2f9e6a]" />

                                    </span>

                                    <span className="text-[36px] font-semibold tracking-[-0.03em] text-[#1f1b19]">

                                         Working

                                    </span>

                                </div>


                                {/* STORE */}

                                {selectedStoreData && (

                                    <p className="mt-6 text-[17px] font-medium text-[#1f1b19]">

                                        {selectedStoreData.name}

                                    </p>

                                )}


                                {/* CLOCK IN DETAILS */}

                                {clockInDate && (

                                    <div className="mt-6 flex flex-col items-center">

                                        <p className="text-[15px] text-[#8a837d]">

                                            Clocked in

                                        </p>

                                        <p className="mt-1 text-[28px] font-semibold tracking-[-0.02em] text-[#1f1b19]">

                                            {formatTime(clockInDate)}

                                        </p>

                                        <p className="mt-1 text-[15px] text-[#8a837d]">

                                            {formatDate(clockInDate)}

                                        </p>

                                    </div>

                                )}

                            </>

                        )}


                        {/* ================================================
                            CLOCKED OUT
                        ================================================= */}

                        {!isClockedIn && (

                            <>

                                {/* STATUS */}

                                <div className="flex items-center justify-center gap-3">

                                    <span className="h-3 w-3 rounded-full bg-[#c9c2bc]" />

                                    <span className="text-[36px] font-semibold tracking-[-0.03em] text-[#1f1b19]">

                                        Not Working

                                    </span>

                                </div>


                                {/* CLOCKED OUT */}

                                {lastTimeEntry?.clockOut ? (

                                    <div className="mt-6 flex flex-col items-center">

                                        <p className="text-[15px] text-[#8a837d]">

                                            Clocked out

                                        </p>

                                        <p className="mt-1 text-[28px] font-semibold tracking-[-0.02em] text-[#1f1b19]">

                                            {formatTime(lastTimeEntry.clockOut)}

                                        </p>

                                        <p className="mt-1 text-[15px] text-[#8a837d]">

                                            {formatDate(lastTimeEntry.clockOut)}

                                        </p>

                                    </div>

                                    ) : (

                                    <div className="mt-6 flex flex-col items-center">

                                        <p className="text-[15px] text-[#8a837d]">

                                            No previous clock-out

                                        </p>

                                    </div>

                                    )}

                            </>

                        )}

                        </section>


                    {/* ====================================================
                        LOCATION / GPS SECTION
                    ==================================================== */}

                    {!isClockedIn && (

                            <section className="mt-[clamp(12px,3vh,28px)]">


                            {/* ============================================
                                LOCATION UNAVAILABLE / BLOCKED
                            ============================================ */}

                            {(isManualMode ||
                            locationPermission === "denied") && (

                                <div className="space-y-4">


                                    <div className="rounded-2xl border border-[#eadfcf] bg-[#fffaf2] px-5 py-4">

                                        <p className="text-[14px] font-semibold text-[#5c4a32]">

                                            Location unavailable

                                        </p>


                                        <p className="mt-1 text-[13px] leading-relaxed text-[#8a837d]">

                                            {locationMessage ||
                                                "Please select the store where you are working."}

                                        </p>


                                        <button
                                            type="button"
                                            onClick={
                                                tryGPSAgain
                                            }
                                            disabled={
                                                isRequestingLocation ||
                                                isProcessing
                                            }
                                            className="mt-4 text-[13px] font-semibold text-[#7a3ea3] disabled:opacity-50"
                                        >

                                            {isRequestingLocation
                                                ? "Checking location..."
                                                : "Try GPS Again"}

                                        </button>

                                    </div>


                                    {/* ======================================
                                        STORE SELECT
                                    ====================================== */}

                                    <div>

                                        <label
                                            htmlFor="store"
                                            className="mb-2 block text-[14px] font-medium text-[#5c5651]"
                                        >

                                            Select store

                                        </label>


                                        {isLoadingStores ? (

                                            <div className="h-[56px] w-full rounded-xl border border-[#dcd6d1] bg-white px-4 py-4 text-sm text-[#8a837d]">

                                                Loading stores...

                                            </div>

                                        ) : (

                                            <select
                                                id="store"
                                                value={
                                                    selectedStore
                                                }
                                                onChange={(e) =>
                                                    setSelectedStore(
                                                        e.target.value
                                                    )
                                                }
                                                className="h-[56px] w-full rounded-xl border border-[#dcd6d1] bg-white px-4 text-[15px] text-[#1f1b19] outline-none focus:border-[#7a3ea3] focus:ring-2 focus:ring-[#7a3ea3]/15"
                                            >

                                                <option value="">

                                                    Select a store

                                                </option>


                                                {stores.map(
                                                    (store) => (

                                                        <option
                                                            key={
                                                                store._id
                                                            }
                                                            value={
                                                                store._id
                                                            }
                                                        >

                                                            {
                                                                store.name
                                                            }

                                                        </option>

                                                    )
                                                )}

                                            </select>

                                        )}


                                        {selectedStoreData && (

                                            <div className="mt-3 rounded-xl bg-[#f2eeea] px-4 py-3">

                                                <p className="text-sm font-semibold text-[#1f1b19]">

                                                    {
                                                        selectedStoreData.name
                                                    }

                                                </p>


                                                <p className="mt-1 text-xs text-[#8a837d]">

                                                    {
                                                        selectedStoreData.address
                                                    }

                                                    {", "}

                                                    {
                                                        selectedStoreData.city
                                                    }

                                                    {", "}

                                                    {
                                                        selectedStoreData.state
                                                    }

                                                </p>

                                            </div>

                                        )}

                                    </div>

                                </div>

                            )}

                        </section>

                    )}


                    {/* ====================================================
                        ACTION AREA
                    ==================================================== */}

<section className="mt-[clamp(8px,2vh,18px)] pt-[clamp(8px,2vh,18px)]">


                        {/* ================================================
                            CLOCK IN
                        ================================================= */}

                        {!isClockedIn && (

                            <button
                                type="button"
                                onClick={
                                    openPinVerification
                                }
                                disabled={

                                    isProcessing ||

                                    isRequestingLocation ||

                                    (

                                        (
                                            isManualMode ||
                                            locationPermission === "denied"
                                        ) &&
                                        !selectedStore

                                    )

                                }
                                className="
                                    flex
                                    h-[64px]
                                    w-full
                                    items-center
                                    justify-center
                                    rounded-[20px]
                                    bg-[#7a3ea3]
                                    text-[18px]
                                    font-semibold
                                    text-white
                                    shadow-sm
                                    transition-all
                                    hover:bg-[#6a3590]
                                    active:scale-[0.985]
                                    disabled:cursor-not-allowed
                                    disabled:bg-[#c9c2bc]
                                "
                            >

                                {isProcessing ||
                                isRequestingLocation

                                    ? "Processing..."

                                    : (

                                        isManualMode ||
                                        locationPermission === "denied"

                                    )

                                        ? "Clock In Manually"

                                        : "Clock In"

                                }

                            </button>

                        )}


                        {/* ================================================
                            CLOCK OUT
                        ================================================= */}

                        {isClockedIn && (

                            <button
                                type="button"
                                onClick={
                                    openPinVerification
                                }
                                disabled={
                                    isProcessing
                                }
                                className="
                                    flex
                                    h-[64px]
                                    w-full
                                    items-center
                                    justify-center
                                    rounded-[20px]
                                    bg-[#1f1b19]
                                    text-[18px]
                                    font-semibold
                                    text-white
                                    shadow-sm
                                    transition-all
                                    hover:bg-black
                                    active:scale-[0.985]
                                    disabled:cursor-not-allowed
                                    disabled:bg-[#8a837d]
                                "
                            >

                                {isProcessing
                                    ? "Processing..."
                                    : "Clock Out"}

                            </button>

                        )}


                                {/* ====================================================
                                    TIME SUMMARY
                                ==================================================== */}

                                <button
                                    type="button"
                                    onClick={getEmployeeTimeSummary}
                                    disabled={isLoadingTimeSummary}
                                    className="
                                        mx-auto
                                        mt-6
                                        block
                                        text-[14px]
                                        font-medium
                                        text-[#7a3ea3]
                                        transition
                                        hover:text-[#6a3590]
                                        disabled:opacity-50
                                    "
                                >
                                    {isLoadingTimeSummary
                                        ? "Loading time entries..."
                                        : "View Time Entries"
                                    }
                                </button>

                    </section>


                </main>


            </div>



            {/* ====================================================
    PIN VERIFICATION MODAL
==================================================== */}

{showPinModal && (

<div className="fixed inset-0 z-[100] flex items-start justify-center overflow-y-auto bg-black/40 px-5 pb-[max(20px,env(safe-area-inset-bottom))] pt-[max(24px,env(safe-area-inset-top))] sm:items-center">

    <div className="my-auto w-full max-w-[390px] max-h-[calc(100dvh-48px)] overflow-y-auto rounded-[28px] bg-white p-5 shadow-2xl sm:p-6">


        {/* ============================================
            HEADER
        ============================================ */}

        <div className="text-center">

            <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-[#f1e8f7] text-[22px] font-bold text-[#7a3ea3]">

                🔒

            </div>


            <h2 className="mt-4 text-[24px] font-semibold tracking-[-0.02em] text-[#1f1b19]">

                Enter your PIN

            </h2>

        </div>


        {/* ============================================
            PIN FORM
        ============================================ */}

        <form
            onSubmit={verifyPinAndClock}
            className="mt-7"
        >

            <input
                type="password"
                ref={pinInputRef}
                value={verificationPin}
                onChange={
                    handleVerificationPinChange
                }
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={4}
                autoComplete="off"
                placeholder="••••"
                disabled={isVerifyingPin}
                className="
                    h-[62px]
                    w-full
                    rounded-2xl
                    border-[1.5px]
                    border-[#dcd6d1]
                    bg-white
                    px-4
                    text-center
                    text-[26px]
                    tracking-[0.4em]
                    text-[#1f1b19]
                    outline-none
                    transition
                    placeholder:text-[#b3aca7]
                    focus:border-[#7a3ea3]
                    focus:ring-[3px]
                    focus:ring-[#7a3ea3]/15
                    disabled:opacity-60
                "
            />


            {/* PIN ERROR */}

            {pinError && (

                <p className="mt-3 text-center text-[14px] font-medium text-red-600">

                    {pinError}

                </p>

            )}


            {/* ========================================
                ACTION BUTTONS
            ========================================= */}

            <div className="mt-6 flex gap-3">


                {/* CANCEL */}

                <button
                    type="button"
                    onClick={
                        closePinVerification
                    }
                    disabled={isVerifyingPin}
                    className="
                        flex
                        h-[56px]
                        flex-1
                        items-center
                        justify-center
                        rounded-[18px]
                        bg-[#f2eeea]
                        text-[16px]
                        font-semibold
                        text-[#5c5651]
                        transition
                        hover:bg-[#e8e2dd]
                        disabled:cursor-not-allowed
                        disabled:opacity-60
                    "
                >

                    Cancel

                </button>


                {/* CONFIRM */}

                <button
                    type="submit"
                    disabled={
                        isVerifyingPin ||
                        verificationPin.length !== 4
                    }
                    className="
                        flex
                        h-[56px]
                        flex-1
                        items-center
                        justify-center
                        rounded-[18px]
                        bg-[#7a3ea3]
                        text-[16px]
                        font-semibold
                        text-white
                        transition
                        hover:bg-[#6a3590]
                        disabled:cursor-not-allowed
                        disabled:bg-[#c9c2bc]
                    "
                >

                    {isVerifyingPin

                        ? "Verifying..."

                        : isClockedIn

                            ? "Clock Out"

                            : "Clock In"

                    }

                </button>

            </div>

        </form>

    </div>

</div>

)}

{/* ====================================================
    CHANGE REQUEST MODAL
==================================================== */}


{/* ====================================================
    CHANGE REQUEST MODAL
==================================================== */}

{showChangeRequestModal && (

<div
className="
    fixed
    inset-0
    z-[100]
    flex
    items-center
    justify-center
    overflow-hidden
    bg-black/40
    px-4
    py-4
    sm:px-6
"
>

<div
    className="
        flex
        w-full
        max-w-[390px]
        max-h-[calc(100dvh-32px)]
        flex-col
        overflow-y-auto
        overscroll-contain
        rounded-[28px]
        bg-white
        p-5
        shadow-2xl
        sm:max-h-[calc(100dvh-48px)]
        sm:p-6
    "
>

            {/* ============================================
                HEADER
            ============================================ */}

            <div className="text-center">

                <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-[#f1e8f7] text-[22px] text-[#7a3ea3]">

                    ✎

                </div>


                <h2 className="mt-4 text-[24px] font-semibold tracking-[-0.02em] text-[#1f1b19]">

                    Fix My Time

                </h2>


                <p className="mt-2 text-[14px] leading-relaxed text-[#8a837d]">

                    Tell us what needs to be fixed.

                </p>

            </div>


            {/* ============================================
                CHANGE REQUEST FORM
            ============================================ */}

            <form
                onSubmit={submitChangeRequest}
                className="mt-7"
            >

                <label
                    htmlFor="changeRequestMessage"
                    className="mb-2 block text-[14px] font-medium text-[#5c5651]"
                >

                    What needs to change?

                </label>


                <textarea
                    id="changeRequestMessage"
                    value={changeRequestMessage}
                    onChange={(e) =>
                        setChangeRequestMessage(
                            e.target.value
                        )
                    }
                    disabled={isSubmittingChangeRequest}
                    placeholder="Example: I forgot to clock out, I left at 6:15 PM on Saturday Sep 17"
                    rows={6}
                    maxLength={1000}
                    className="
                        w-full
                        resize-none
                        rounded-2xl
                        border-[1.5px]
                        border-[#dcd6d1]
                        bg-white
                        px-4
                        py-4
                        text-[15px]
                        leading-relaxed
                        text-[#1f1b19]
                        outline-none
                        transition
                        placeholder:text-[#b3aca7]
                        focus:border-[#7a3ea3]
                        focus:ring-[3px]
                        focus:ring-[#7a3ea3]/15
                        disabled:opacity-60
                    "
                />


                <div className="mt-2 flex justify-end">

                    <span className="text-[12px] text-[#a39b95]">

                        {changeRequestMessage.length}/1000

                    </span>

                </div>


                {/* ========================================
                    ACTION BUTTONS
                ========================================= */}

                <div className="mt-5 flex gap-3">


                    {/* CANCEL */}

                    <button
                        type="button"
                        onClick={closeChangeRequestModal}
                        disabled={isSubmittingChangeRequest}
                        className="
                            flex
                            h-[56px]
                            flex-1
                            items-center
                            justify-center
                            rounded-[18px]
                            bg-[#f2eeea]
                            text-[16px]
                            font-semibold
                            text-[#5c5651]
                            transition
                            hover:bg-[#e8e2dd]
                            disabled:cursor-not-allowed
                            disabled:opacity-60
                        "
                    >

                        Cancel

                    </button>


                    {/* SUBMIT */}

                    <button
                        type="submit"
                        disabled={
                            isSubmittingChangeRequest ||
                            !changeRequestMessage.trim()
                        }
                        className="
                            flex
                            h-[56px]
                            flex-1
                            items-center
                            justify-center
                            rounded-[18px]
                            bg-[#7a3ea3]
                            text-[16px]
                            font-semibold
                            text-white
                            transition
                            hover:bg-[#6a3590]
                            disabled:cursor-not-allowed
                            disabled:bg-[#c9c2bc]
                        "
                    >

                        {isSubmittingChangeRequest
                            ? "Sending..."
                            : "Send Request"
                        }

                    </button>

                </div>

            </form>

        </div>

    </div>

)}

{/* ============================================================
    MY TIME ENTRIES MODAL
============================================================ */}

{showTimeSummaryModal && (

<div
    className="
        fixed
        inset-0
        z-[100]
        flex
        items-center
        justify-center
        bg-black/40
        px-4
        py-4
        sm:px-6
    "
>

    <div
        className="
            flex
            w-full
            max-w-[720px]
            flex-col
            overflow-hidden
            rounded-[28px]
            bg-white
            shadow-2xl
            max-h-[calc(100dvh-32px)]
            sm:max-h-[calc(100dvh-48px)]
        "
    >

        {/* ==================================================
            HEADER
        ================================================== */}

        <div
            className="
                shrink-0
                border-b
                border-[#eee9e5]
                px-5
                py-5
                sm:px-7
                sm:py-6
            "
        >

            <div
                className="
                    flex
                    items-center
                    justify-between
                    gap-4
                "
            >

                {/* TITLE */}

                <div>

                    <h2
                        className="
                            text-[23px]
                            font-semibold
                            tracking-[-0.02em]
                            text-[#1f1b19]
                            sm:text-[26px]
                        "
                    >
                        My Time Entries
                    </h2>

                </div>


                {/* CLOSE X */}

                <button
                    type="button"
                    onClick={() =>
                        setShowTimeSummaryModal(false)
                    }
                    aria-label="Close"
                    className="
                        flex
                        h-11
                        w-11
                        shrink-0
                        items-center
                        justify-center
                        rounded-full
                        bg-[#f2eeea]
                        text-[26px]
                        font-normal
                        leading-none
                        text-[#6f6862]
                        transition
                        hover:bg-[#e8e2dd]
                        hover:text-[#3f3935]
                    "
                >
                    ×
                </button>

            </div>

        </div>


        {/* ==================================================
            SCROLLABLE CONTENT
        ================================================== */}

        <div
            className="
                min-h-0
                flex-1
                overflow-y-auto
                px-5
                py-5
                sm:px-7
                sm:py-6
            "
        >

            <div className="space-y-5">

                {getTimeSummaryWeeks(
                    employeeTimeSummary?.days
                ).map(
                    (
                        week
                    ) => (

                        <section
                            key={
                                week.label
                            }
                        >

                            {/* ==================================
                                WEEK HEADER
                            ================================== */}

                            <div
                                className="
                                    rounded-[14px]
                                    bg-[#f1f3f5]
                                    px-5
                                    py-3
                                "
                            >

                            <p
                                className="
                                    text-[16px]
                                    font-semibold
                                    tracking-[-0.01em]
                                    text-[#1f1b19]
                                "
                            >
                                {week.label}
                            </p>

                            </div>


                            {/* ==================================
                                DAYS
                            ================================== */}

                            <div className="mt-3 space-y-2">

                                {week.days.map(
                                    (
                                        day
                                    ) => {

                                        const {
                                            weekday,
                                            monthDay,
                                        } =
                                            formatSummaryDay(
                                                day.date
                                            );


                                        return (

                                            <div
                                                key={
                                                    day.date
                                                }
                                                className="
                                                    rounded-[16px]
                                                    border
                                                    border-[#e8e2dd]
                                                    bg-white
                                                    px-4
                                                    py-4
                                                    sm:px-5
                                                "
                                            >

                                                <div
                                                    className="
                                                        flex
                                                        items-center
                                                        justify-between
                                                        gap-4
                                                    "
                                                >

                                                    {/* ==================================
                                                        DATE
                                                    ================================== */}

                                                    <div
                                                        className="
                                                            min-w-[80px]
                                                            shrink-0
                                                        "
                                                    >

                                                        <p
                                                            className="
                                                                text-[18px]
                                                                font-semibold
                                                                leading-tight
                                                                text-[#1f1b19]
                                                            "
                                                        >
                                                            {weekday}
                                                        </p>


                                                        <p
                                                            className="
                                                                mt-0.5
                                                                text-[16px]
                                                                leading-tight
                                                                text-[#716a65]
                                                            "
                                                        >
                                                            {monthDay}
                                                        </p>

                                                    </div>


                                                    {/* ==================================
                                                        ENTRIES
                                                    ================================== */}

                                                    <div
                                                        className="
                                                            flex
                                                            min-w-0
                                                            flex-1
                                                            flex-col
                                                            items-end
                                                            gap-1
                                                        "
                                                    >

                                                        {day.entries &&
                                                        day.entries.length >
                                                            0 ? (

                                                            day.entries.map(
                                                                (
                                                                    entry,
                                                                    index
                                                                ) => (

                                                                    <div
                                                                        key={
                                                                            entry.id ||
                                                                            index
                                                                        }
                                                                        className="
                                                                            text-right
                                                                            text-[15px]
                                                                            font-medium
                                                                            leading-tight
                                                                            text-[#29252d]
                                                                            sm:text-[16px]
                                                                        "
                                                                    >

                                                                        <span>
                                                                            {entry.clockIn
                                                                                ? formatTime(
                                                                                      entry.clockIn
                                                                                  )
                                                                                : "--"}

                                                                            {" — "}

                                                                            {entry.clockOut
                                                                                ? formatTime(
                                                                                      entry.clockOut
                                                                                  )
                                                                                : "Open"}
                                                                        </span>

                                                                    </div>

                                                                )
                                                            )

                                                        ) : (

                                                            <span
                                                                className="
                                                                    text-[14px]
                                                                    text-[#a39b95]
                                                                "
                                                            >
                                                                No entries
                                                            </span>

                                                        )}

                                                    </div>

                                                </div>

                                            </div>

                                        );

                                    }
                                )}

                            </div>

                        </section>

                    )
                )}


                {/* ==========================================
                    NO DATA
                ========================================== */}

                {(
                    !employeeTimeSummary?.days ||
                    employeeTimeSummary.days.length === 0
                ) && (

                    <div
                        className="
                            rounded-2xl
                            border
                            border-[#e8e2dd]
                            px-5
                            py-8
                            text-center
                        "
                    >

                        <p
                            className="
                                text-[14px]
                                text-[#8a837d]
                            "
                        >
                            No time entries found.
                        </p>

                    </div>

                )}

            </div>

        </div>


        {/* ==================================================
            FIXED FOOTER
        ================================================== */}

        <div
            className="
                shrink-0
                space-y-2
                border-t
                border-[#eee9e5]
                bg-white
                px-5
                py-4
                sm:px-7
            "
        >

            {/* ==============================================
                REQUEST TIME CHANGE
            ============================================== */}

            <button
                type="button"
                onClick={() => {

                    setShowTimeSummaryModal(
                        false
                    );

                    openChangeRequestModal();

                }}
                className="
                    flex
                    h-[50px]
                    w-full
                    items-center
                    justify-center
                    rounded-[16px]
                    bg-[#f2eeea]
                    text-[15px]
                    font-semibold
                    text-[#5c5651]
                    transition
                    hover:bg-[#e8e2dd]
                "
            >
                Request time change
            </button>


            {/* ==============================================
                CLOSE
            ============================================== */}

            <button
                type="button"
                onClick={() =>
                    setShowTimeSummaryModal(
                        false
                    )
                }
                className="
                    flex
                    h-[52px]
                    w-full
                    items-center
                    justify-center
                    rounded-[16px]
                    bg-[#1f1f1f]
                    text-[16px]
                    font-semibold
                    text-white
                    transition
                    hover:bg-black
                "
            >
                Close
            </button>

        </div>

    </div>

</div>

)}




        </div>

    );

};